import {
  IAnimateLineAddParams,
  IAnimateLineDelParams,
  IResult,
} from "@/types/map";
import { loadModules } from "esri-loader";
// import { mat3, vec2, vec3 } from "gl-matrix";
// import * as turf from "@turf/helpers";
// import midpoint from "@turf/midpoint";

/**
 * 动态线
 */
export default class AnimateLine {
  private static intances: Map<string, any>;

  private view: any;
  private AnimationLayer: any; // 自定义的动画图层类，属于GraphicsLayer的子类

  private constructor(view: __esri.MapView | __esri.SceneView) {
    this.view = view;
  }

  public static getInstance(view: any) {
    let id = view.container.id;
    if (!AnimateLine.intances) {
      AnimateLine.intances = new Map();
    }
    let intance = AnimateLine.intances.get(id);
    if (!intance) {
      intance = new AnimateLine(view);
      AnimateLine.intances.set(id, intance);
    }
    return intance;
  }

  // 添加动态线
  public async addOne(params: IAnimateLineAddParams): Promise<IResult> {
    if (!this.AnimationLayer) {
      await this.initAnimationLayer();
    }
    const [Polyline] = await loadModules(["esri/geometry/Polyline"]);
    // 解析参数
    const lines = params.lines;
    const repeat = params.repeat || true;
    const speed = params.speed || 100;

    // 构造graphics
    const graphics = lines.paths.map((path: any) => {
      return {
        attributes: {
          repeat: repeat,
          speed: speed,
        },
        symbol: {
          type: "simple-line",
          color: lines.color || "white",
          width: lines.width || 1,
          style: lines.style || "solid",
        },
        geometry: new Polyline({
          paths: [path],
        }),
      };
    });

    // 创建动态线图层
    const lineLayer = new this.AnimationLayer({
      graphics: graphics,
      title: params.type || "default",
    });
    this.view.map.layers.add(lineLayer);
    return {
      status: 0,
      message: "add success",
    };
  }

  // 删除动态线
  // @notice 关于图层数量的变化： 动态图层实际上为两个图层，第二个图层即位显示的效果图层，该图层的添加和删除相较于第一个图层均有一定延迟
  public async delete(params: IAnimateLineDelParams): Promise<IResult> {
    // 遍历图层，删除指定图层
    let count = 0;
    this.view.map.layers.forEach((layer: any) => {
      if (layer.type == "graphics" && params.types.indexOf(layer.title) >= 0) {
        this.view.map.layers.remove(layer);
        ++count;
      }
    });
    return {
      status: 0,
      message: `delete ${count} success`,
      params: params,
    };
  }

  private async initAnimationLayer() {
    const [
      esriRequest,
      webMercatorUtils,
      Graphic,
      GraphicsLayer,
      BaseLayerViewGL2D,
      Polyline,
      SimpleLineSymbol,
    ] = await loadModules([
      "esri/request",
      "esri/geometry/support/webMercatorUtils",
      "esri/Graphic",
      "esri/layers/GraphicsLayer",
      "esri/views/2d/layers/BaseLayerViewGL2D",
      "esri/geometry/Polyline",
      "esri/symbols/SimpleLineSymbol",
    ]);
    // Subclass the custom layer view from BaseLayerViewGL2D.
    const CustomLayerView2D = BaseLayerViewGL2D.createSubclass({
      constructor: function (da: any) {
        //线插值密度，决定了线的动态速度，密度越大速度越慢. 应当保持在[10,100000]区间内，显示效果比较理想
        this.density = 500;
        //每条线的帧率计数器.每帧更新一个点
        this.counts = [];
        //插值后的路线集
        this.paths = [];
        //动态线效果的图层
        this.linesLayer = undefined;
        //是否在更新动态效果
        this.condition = true;
        //是否重复动画效果
        this.repeat = true;
      },

      // Called once a custom layer is added to the map.layers collection and this layer view is instantiated.
      attach: function () {
        const that = this;
        // 初始化一些参数
        const graphics = that.layer.graphics;
        this.density = 100000 / (graphics.items[0].attributes.speed as number);
        this.repeat = graphics.items[0].attributes.repeat;
        // 将线插值获取连续点集
        for (let i = 0; i < graphics.items.length; ++i) {
          const graphic = graphics.items[i];
          const path = graphic.geometry.paths[0]; //默认认为传入的每个polyline只有一个path
          let lastPoint = path[0];
          let tmp_path = new Array<[number, number]>();
          for (let j = 0; j < path.length; ++j) {
            // 两点之间线性插值
            tmp_path = tmp_path.concat(
              that.linearInterpolation(lastPoint, path[j], that.density)
            );
            lastPoint = path[j];
          }
          that.paths.push(tmp_path);
        }
        // 构造一个长度与this.paths相同的空二维数组
        const empty_path: any[] = [];
        that.paths.forEach(() => {
          const empty_arr = new Array<[number, number]>();
          empty_path.push(empty_arr);
          // 初始化计数器
          this.counts.push(0);
        });
        // 初始化动态线图层，设置线的样式，并添加到地图上
        that.linesLayer = new GraphicsLayer({
          graphics: [
            {
              geometry: new Polyline({
                paths: empty_path,
              }),
              symbol: this.layer.graphics.getItemAt(0).symbol,
            },
          ],
        });
        that.view.map.layers.add(that.linesLayer);
      },

      // Called once a custom layer is removed from the map.layers collection and this layer view is destroyed.
      detach: function () {
        this.view.map.layers.remove(this.linesLayer);
        this.density = 500;
        this.counts = [];
        this.paths = [];
        this.linesLayer = undefined;
        this.condition = true;
        this.repeat = true;
      },

      // Called every time a frame is rendered.
      //@notic 理论上应该使用webgl绘制线，目前由于技术力有限，使用另一种方法：
      // 添加另一个图层，每次render的时候更新这个图层的数据内容
      render: function (renderParameters: any) {
        if (this.linesLayer.visible == false) {
          this.linesLayer.visible = true;
          this.view.map.layers.add(this.linesLayer);
        }
        // 结束动态渲染
        if (this.condition && this.counts[0] > 0) {
          this.linesLayer.graphics = this.layer.graphics.clone();
          return;
        }
        const that = this;
        const _condition = new Map<number, boolean>(); //线的更新状态
        // 每次渲染更新线的坐标(只有一个graphic)
        let graphic = that.linesLayer.graphics.getItemAt(0);
        for (let i = 0; i < that.paths.length; ++i) {
          const path = that.paths[i];
          // 这一条线的所有点都添加完毕
          if (graphic.geometry.paths[i].length > path.length) {
            _condition.set(i, true);
            //是否重复动画
            if (that.repeat) {
              _condition.set(i, false);
              graphic.geometry.paths[i] = [];
              that.counts[i] = 0;
              // 防止残留
              that.linesLayer.visible = false;
              that.view.map.layers.remove(that.linesLayer);
            }
            //@notice 当不重复动画时，这里有个bug，paths中每条路线长度应该相同，如果不相同则当最短的绘制完成后，剩下的将不会绘制
            // 暂时没找到bug为什么存在，奇了怪了
          } else {
            //更新计数器
            ++that.counts[i];
            _condition.set(i, false);
            // 把点推入渲染
            graphic.geometry.paths[i].push(path[that.counts[i] - 1]);
          }
        }
        // 更新动画图层上的graphic
        this.linesLayer.graphics.removeAll();
        this.linesLayer.graphics.add(
          new Graphic({
            geometry: graphic.geometry,
            symbol: graphic.symbol,
          })
        );
        // 若不重复动画，则当所有线更新完毕后，最后一次渲染把线简化
        this.condition = true;
        _condition.forEach((v) => {
          this.condition = this.condition && v;
        });
        // Request new render because markers are animated.
        this.requestRender();
      },

      // Called internal in render()
      // 两点之间线性插值，返回一条路线
      linearInterpolation(
        startPoint: [number, number],
        endPoint: [number, number],
        amount: number
      ): Array<[number, number]> {
        if (startPoint[0] == endPoint[0] && startPoint[1] == endPoint[1]) {
          return [startPoint];
        }
        const points = new Array<[number, number]>();
        const deltaLon = (endPoint[0] - startPoint[0]) / amount;
        const deltaLat = (endPoint[1] - startPoint[1]) / amount;
        for (let i = 0; i < amount; ++i) {
          points.push([
            startPoint[0] + deltaLon * i,
            startPoint[1] + deltaLat * i,
          ]);
        }
        return points;
      },
    });

    // Subclass the layer view from GraphicsLayer, to take advantage of its
    // watchable graphics property.
    this.AnimationLayer = GraphicsLayer.createSubclass({
      createLayerView: function (view: any) {
        if (view.type === "2d") {
          return new CustomLayerView2D({
            view: view,
            layer: this,
          });
        }
      },
    });
  }

  // private _getMidpoint(
  //   s: [number, number],
  //   e: [number, number],
  //   count: number
  // ): [number, number] {
  //   const point1 = turf.point(s);
  //   const point2 = turf.point(e);
  //   const mp = midpoint(point1, point2);
  //   console.log(mp.geometry.coordinates);
  //   return [0, 0];
  // }
}
